import React, { useEffect, useMemo, useState } from 'react';
import { Matiere } from '../Devis/ConfigurateurArticle/types';
import ColorNameInput from '../../data/ColorNameInput';

interface MatiereModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (matiere: MatiereModalFormData) => Promise<void>;
  matiere?: Matiere | null;
  typesMatiere: { id: string; label: string }[];
}

export type MatiereModalFormData = Omit<Matiere, 'id'> & { id?: string };

const normalizeCouleurEntry = (c: any) =>
  typeof c === 'string' ? { nom: c, hex: '' } : { nom: c?.nom || '', hex: c?.hex || '' };

const createInitialFormData = (matiere: Matiere | null | undefined, fallbackType: string): MatiereModalFormData => ({
  id: matiere?.id,
  nom: matiere?.nom || '',
  type: matiere?.type || fallbackType,
  laize: matiere?.laize ?? 140,
  prix: matiere?.prix ?? 0,
  remiseFournisseur: matiere?.remiseFournisseur ?? 50,
  minimumCommande: matiere?.minimumCommande ?? 1,
  couleurs: (
    (
      ((matiere as any)?._couleursNormalisees as any[]) ||
      (matiere?.couleurs as any[]) ||
      []
    ).map(normalizeCouleurEntry) as unknown
  ) as Matiere['couleurs'],
  usage_indoor: matiere?.usage_indoor ?? false,
  usage_outdoor: matiere?.usage_outdoor ?? false,
  usage_nautisme: matiere?.usage_nautisme ?? false,
  usage_medical: matiere?.usage_medical ?? false,
  fournisseur: matiere?.fournisseur,
  favori: matiere?.favori,
  url_produit: matiere?.url_produit,
});

const MatiereModal: React.FC<MatiereModalProps> = ({ open, onClose, onSave, matiere, typesMatiere }) => {
  const defaultType = useMemo(() => typesMatiere[0]?.id || '', [typesMatiere]);
  const [formData, setFormData] = useState<MatiereModalFormData>(() => createInitialFormData(matiere, defaultType));
  const [couleurInput, setCouleurInput] = useState('');
  const [nouvelleCouleurHex, setNouvelleCouleurHex] = useState('#cccccc');
  const [couleurHexManuelle, setCouleurHexManuelle] = useState(false);
  const [saving, setSaving] = useState(false);
  const couleurTrim = couleurInput.trim();
  const boutonLabel = couleurTrim ? `+ Ajouter "${couleurTrim}"` : 'Ajouter';
  const boutonDisabled = !couleurTrim;

  useEffect(() => {
    setFormData(createInitialFormData(matiere, defaultType));
    setCouleurInput('');
    setNouvelleCouleurHex('#cccccc');
    setCouleurHexManuelle(false);
  }, [matiere, defaultType, open]);

  const handleSave = async () => {
    if (!formData.nom || !formData.type || !formData.laize || !formData.prix) {
      alert('Veuillez remplir tous les champs obligatoires');
      return;
    }

    try {
      setSaving(true);
      await onSave({
        ...formData,
        minimumCommande: formData.minimumCommande ? Math.max(formData.minimumCommande, 0.1) : 1,
      });
      onClose();
    } finally {
      setSaving(false);
    }
  };

  const getCouleurNom = (couleur: any): string => (typeof couleur === 'string' ? couleur : couleur?.nom || '');
  const getCouleurHex = (couleur: any): string => (typeof couleur === 'string' ? '' : couleur?.hex || '');

  const addCouleur = () => {
    const value = couleurInput.trim();
    if (!value) return;
    setFormData({
      ...formData,
      couleurs: ([...(formData.couleurs || []), { nom: value, hex: nouvelleCouleurHex || '' }] as unknown) as Matiere['couleurs'],
    });
    setCouleurInput('');
    setNouvelleCouleurHex('#cccccc');
    setCouleurHexManuelle(false);
  };

  const removeCouleur = (couleur: Matiere['couleurs'][number]) => {
    const targetNom = getCouleurNom(couleur);
    setFormData({
      ...formData,
      couleurs: (formData.couleurs || []).filter((c) => getCouleurNom(c) !== targetNom),
    });
  };

  if (!open) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900 bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-5xl max-h-[90vh] min-h-[65vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">
              {matiere ? 'Modifier une matière' : 'Nouvelle matière'}
            </p>
            <h3 className="text-lg font-semibold text-gray-900">{matiere ? matiere.nom : 'Ajouter une matière'}</h3>
          </div>
          <button className="text-gray-400 hover:text-gray-600" onClick={onClose} aria-label="Fermer la modale">
            ✕
          </button>
        </div>

        <div className="px-6 py-4 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <label htmlFor="nom" className="form-label">
                Nom
              </label>
              <input
                id="nom"
                type="text"
                className="input-field"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Nom de la matière"
              />
            </div>
            <div>
              <label htmlFor="type" className="form-label">
                Type
              </label>
              <select
                id="type"
                className="input-field"
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              >
                {typesMatiere.map((type) => (
                  <option key={type.id} value={type.id}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="laize" className="form-label">
                Laize (cm)
              </label>
              <input
                id="laize"
                type="number"
                className="input-field"
                value={formData.laize}
                onChange={(e) => setFormData({ ...formData, laize: Number(e.target.value) })}
                placeholder="140"
              />
            </div>
            <div>
              <label htmlFor="prix" className="form-label">
                Prix TTC / ML (€)
              </label>
              <input
                id="prix"
                type="number"
                step="0.01"
                className="input-field"
                value={formData.prix}
                onChange={(e) => setFormData({ ...formData, prix: Number(e.target.value) })}
                placeholder="0"
              />
            </div>
            <div>
              <label htmlFor="remise" className="form-label">
                Remise fournisseur (%)
              </label>
              <input
                id="remise"
                type="number"
                step="1"
                min="0"
                max="100"
                className="input-field"
                value={formData.remiseFournisseur ?? 0}
                onChange={(e) => setFormData({ ...formData, remiseFournisseur: Number(e.target.value) })}
                placeholder="50"
              />
            </div>
            <div>
              <label htmlFor="minimumCommande" className="form-label">
                Minimum commande (ML)
              </label>
              <input
                id="minimumCommande"
                type="number"
                step="0.1"
                min="0.1"
                className="input-field"
                value={formData.minimumCommande ?? 1}
                onChange={(e) => setFormData({ ...formData, minimumCommande: Number(e.target.value) })}
                placeholder="1"
              />
            </div>
          </div>

          <div>
            <label className="form-label">Couleurs disponibles</label>
            <div className="flex flex-wrap gap-2 mb-3">
              {(formData.couleurs || []).map((couleur, idx) => (
                <span
                  key={`${getCouleurNom(couleur)}-${idx}`}
                  className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                >
                  <span
                    className="w-3 h-3 rounded-full border border-blue-200 mr-1.5 inline-block"
                    style={{
                      backgroundColor: getCouleurHex(couleur) || 'transparent',
                      borderColor: getCouleurHex(couleur) ? 'transparent' : '#cbd5e1',
                    }}
                    title={getCouleurHex(couleur) || 'Aucune couleur définie'}
                  />
                  {getCouleurNom(couleur)}
                  <button
                    type="button"
                    className="ml-1.5 inline-flex text-blue-400 hover:text-blue-600"
                    onClick={() => removeCouleur(couleur)}
                  >
                    ✕
                  </button>
                </span>
              ))}
            </div>
            <div className="flex gap-2 items-start">
              <ColorNameInput
                value={couleurInput}
                onChange={(nom: string, hex: string | null) => {
                  setCouleurInput(nom);
                  if (!couleurHexManuelle && hex) {
                    setNouvelleCouleurHex(hex);
                  }
                }}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => e.key === 'Enter' && addCouleur()}
                className="input-field flex-1"
                placeholder="Ajouter une couleur"
              />
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  className="w-12 h-10 border-2 border-gray-300 rounded cursor-pointer hover:ring-2 hover:ring-blue-400"
                  value={nouvelleCouleurHex}
                  onChange={(e) => {
                    setNouvelleCouleurHex(e.target.value);
                    setCouleurHexManuelle(true);
                  }}
                  title="Choisir une couleur"
                />
                <span className="flex items-center gap-1 text-[11px] text-gray-600">
                  <span aria-hidden="true">✎</span>
                  <span>Modifier la couleur</span>
                </span>
              </div>
              <button
                type="button"
                className="btn-primary whitespace-nowrap px-4 py-2 rounded-md bg-blue-100 text-blue-700 border border-blue-200 hover:bg-blue-200 disabled:opacity-50"
                onClick={addCouleur}
                disabled={boutonDisabled}
              >
                {boutonLabel}
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-2">
              Vous pouvez inclure la référence fournisseur dans le nom (ex : PT0650 - Vanilla). Ce libellé sera utilisé
              tel quel dans le bon de commande fournisseur.
            </p>
          </div>

          <div>
            <p className="form-label">Usage</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.usage_indoor || false}
                  onChange={(e) => setFormData({ ...formData, usage_indoor: e.target.checked })}
                />
                <span className="ml-2 text-sm text-gray-700">🏠 Indoor</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.usage_outdoor || false}
                  onChange={(e) => setFormData({ ...formData, usage_outdoor: e.target.checked })}
                />
                <span className="ml-2 text-sm text-gray-700">⛱️ Outdoor</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.usage_nautisme || false}
                  onChange={(e) => setFormData({ ...formData, usage_nautisme: e.target.checked })}
                />
                <span className="ml-2 text-sm text-gray-700">⛵ Nautisme</span>
              </label>
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  checked={formData.usage_medical || false}
                  onChange={(e) => setFormData({ ...formData, usage_medical: e.target.checked })}
                />
                <span className="ml-2 text-sm text-gray-700">🩺 Médical</span>
              </label>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3 bg-gray-50 rounded-b-lg">
          <button
            type="button"
            className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-100"
            onClick={onClose}
            disabled={saving}
          >
            Annuler
          </button>
          <button type="button" className="btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? 'Enregistrement...' : 'Enregistrer la matière'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MatiereModal;
